import { GoogleSigninButtonModule } from '@abacritt/angularx-social-login';
import { A11yModule } from '@angular/cdk/a11y';
import { CdkStepperModule } from '@angular/cdk/stepper';
import { CommonModule, DatePipe } from '@angular/common';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { NgModule } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { GoogleMapsModule } from '@angular/google-maps';
import { MAT_DIALOG_DEFAULT_OPTIONS } from '@angular/material/dialog';
import { RouterModule } from '@angular/router';
import { ContactDetailsComponent } from '@components/contact-details/contact-details.component';
import { CustomCalendarComponent, CustomCalendarHeaderComponent } from '@components/custom-calendar/custom-calendar.component';
import {
  CustomTabBodyComponent,
  CustomTabBodyHostDirective,
  CustomTabComponent,
  CustomTabContainerComponent,
} from '@components/custom-tab-container/custom-tab-container.component';
import { DoctorCardComponent } from '@components/doctor-card/doctor-card.component';
import { InputFieldComponent } from '@components/input-field/input-field.component';
import { LoaderComponent } from '@components/loader/loader.component';
import { MemberInformationComponent } from '@components/member-information/member-information.component';
import { PaymentsComponent } from '@components/payments/payments.component';
import { ViewPaymentOptionsComponent } from '@components/payments/view-payment/options/view-payment-options.component';
import { PaymentDialogComponent, ViewPaymentComponent } from '@components/payments/view-payment/view-payment.component';
import { PictureComponent } from '@components/picture/picture.component';
import { PracticeCardComponent } from '@components/practice-card/practice-card.component';
import { ResetPasswordComponent } from '@components/reset-password/reset-password.component';
import { SidebarNavComponent } from '@components/sidebar-nav/sidebar-nav.component';
import {
  StepsHeaderCompleteDirective,
  StepsHeaderComponent,
  StepsHeaderLabelDirective,
  StepsHeaderStepComponent,
} from '@components/steps-header/steps-header.component';
import { SwipeNotificationsComponent } from '@components/swipe-notifications/swipe-notifications.component';
import { VerticalStepperComponent } from '@components/vertical-stepper/vertical-stepper.component';
import { AboutDialogComponent } from '@dialogs/about-dialog/about-dialog.component';
import { CameraComponent } from '@dialogs/camera/camera.component';
import { FeedbackDialogComponent } from '@dialogs/feedback-dialog/feedback-dialog.component';
import { InputDialogComponent } from '@dialogs/input-dialog/input-dialog.component';
import { LoginDialogComponent } from '@dialogs/login-dialog/login-dialog.component';
import { MessageDialogComponent } from '@dialogs/message-dialog/message-dialog.component';
import { TermsAndConditionsDialogComponent } from '@dialogs/terms-and-conditions-dialog/terms-and-conditions-dialog.component';
import { TimeoutDialogComponent } from '@dialogs/timeout-dialog/timeout-dialog.component';
import { AutofocusDirective } from '@directives/autofocus.directive';
import { FormFieldDirective } from '@directives/form-field.directive';
import { InputMaskDirective } from '@directives/input-mask.directive';
import { LogButtonDirective } from '@directives/log-button.directive';
import { NoAutocompleteDirective } from '@directives/no-autocomplete.directive';
import { NullOnEmptyDirective } from '@directives/null-on-empty.directive';
import { ScrollContainerDirective } from '@directives/scroll-container.directive';
import { DoctorReviewCardComponent } from '@modules/doctor/components/doctor-review-card/doctor-review-card.component';
import { AllergiesComponent } from '@modules/health/components/allergies/allergies.component';
import { ChronicComponent } from '@modules/health/components/chronic/chronic.component';
import { FamilyHistoryComponent } from '@modules/health/components/family-history/family-history.component';
import { GeneralHealthComponent } from '@modules/health/components/general-health/general-health.component';
import { OccupationalHistoryComponent } from '@modules/health/components/occupational-history/occupational-history.component';
import { OperationHistoryComponent } from '@modules/health/components/operation-history/operation-history.component';
import { TravelHistoryComponent } from '@modules/health/components/travel-history/travel-history.component';
import { TreatmentHistoryComponent } from '@modules/health/components/treatment-history/treatment-history.component';
import { VaccinationsComponent } from '@modules/health/components/vaccinations/vaccinations.component';
import { MedicalHistoryComponent } from '@modules/health/pages/medical-history/medical-history.component';
import { TranslateModule } from '@ngx-translate/core';
import { AddressFilterPipe } from '@pipes/address-filter.pipe';
import { CustomDatePipe } from '@pipes/custom-date.pipe';
import { DistancePipe } from '@pipes/distance.pipe';
import { SafePipe } from '@pipes/safe.pipe';
import { TodayTomorrowPipe } from '@pipes/today-tomorrow.pipe';
import { AngularSplitModule } from 'angular-split';
import { WebcamModule } from 'ngx-webcam';
import { MaterialModule } from '../material/material.module';
import { BreadcrumbBarComponent } from './breadcrumb-bar/breadcrumb-bar.component';
import { AddToCalendarComponent } from './components/add-to-calendar/add-to-calendar.component';
import { AddressComponent } from './components/address/address.component';
import { BetaUserComponent } from './components/beta-user/beta-user.component';
import { BookingDisplayCardComponent } from './components/booking-display-card/booking-display-card.component';
import { BundleDisplayCardComponent } from './components/bundle-display-card/bundle-display-card';
import { CommunicationActionsComponent } from './components/communication-actions/communication-actions.component';
import { ContactButtonComponent } from './components/contact-button/contact-button.component';
import { DoctorBookingCardComponent } from './components/doctor-booking-card/doctor-booking-card.component';
import { DoctorListComponent } from './components/doctor-list/doctor-list.component';
import { DrawableImageComponent } from './components/drawable-image/drawable-image.component';
import { GoogleLocationComponent } from './components/google-location/google-location.component';
import { GrowerComponent } from './components/grower/grower.component';
import { LoggedOutHeaderComponent } from './components/logged-out-header/logged-out-header.component';
import { LoginFormComponent } from './components/login-form/login-form.component';
import { LoginComponent } from './components/login-form/login/login.component';
import { LoginSplashComponent } from './components/login-splash/login-splash.component';
import { MedicalAidDetailsComponent } from './components/medical-aid-details/medical-aid-details.component';
import { NoteBannerComponent } from './components/note-banner/note-banner.component';
import { NotificationDetailComponent } from './components/notification-detail/notification-detail.component';
import { NotificationFloaterComponent } from './components/notification-floater/notification-floater.component';
import { NotificationListComponent } from './components/notification-list/notification-list.component';
import { PaymentDisplayCardComponent } from './components/payment-display-card/payment-display-card.component';
import { PeachpayEmbeddingComponent } from './components/payments/view-payment/peachpay-embeddeding/peachpay-embeddeding.component';
import { ProfilePictureComponent } from './components/profile-picture/profile-picture.component';
import { SearchInputComponent } from './components/search-input/search-input.component';
import { SignatureInputComponent } from './components/signature-input/signature-input.component';
import { TermsAndConditionsComponent } from './components/terms-and-conditions/terms-and-conditions.component';
import { TranslateComponent } from './components/translate/translate.component';
import { TypeFilterComponent } from './components/type-filter/type-filter.component';
import { DeleteAccountComponent } from './dialogs/delete-account/delete-account.component';
import { RetryDialogComponent } from './dialogs/retry-dialog/retry-dialog.component';
import { RosterPatientDialogComponent } from './dialogs/roster-patient-dialog/roster-patient-dialog.component';
import { SameDayBookingDialogComponent } from './dialogs/same-day-booking-dialog/same-day-booking-dialog.component';
import { TypeFilterDialogComponent } from './dialogs/type-filter-dialog/type-filter-dialog.component';
import { WhyFaceIdDialogComponent } from './dialogs/why-face-id-dialog/why-face-id-dialog.component';

@NgModule({
  declarations: [
    AboutDialogComponent,
    AddressFilterPipe,
    DistancePipe,
    InputDialogComponent,
    LoginDialogComponent,
    FeedbackDialogComponent,
    TodayTomorrowPipe,
    CustomDatePipe,
    ScrollContainerDirective,
    NullOnEmptyDirective,
    NoAutocompleteDirective,
    FormFieldDirective,
    InputFieldComponent,
    LoaderComponent,
    MessageDialogComponent,
    VerticalStepperComponent,
    DoctorCardComponent,
    PracticeCardComponent,
    CustomCalendarComponent,
    CustomCalendarHeaderComponent,
    CustomTabContainerComponent,
    CustomTabComponent,
    CustomTabBodyComponent,
    CustomTabBodyHostDirective,
    StepsHeaderComponent,
    StepsHeaderStepComponent,
    StepsHeaderCompleteDirective,
    StepsHeaderLabelDirective,
    SidebarNavComponent,
    SearchInputComponent,
    AutofocusDirective,
    SafePipe,
    CommunicationActionsComponent,
    GrowerComponent,
    ContactButtonComponent,
    TypeFilterComponent,
    NotificationListComponent,
    NotificationDetailComponent,
    NotificationFloaterComponent,
    LoginSplashComponent,
    LoginFormComponent,
    DoctorBookingCardComponent,
    DoctorListComponent,
    ProfilePictureComponent,
    TypeFilterDialogComponent,
    LoggedOutHeaderComponent,
    BreadcrumbBarComponent,
    TermsAndConditionsDialogComponent,
    TermsAndConditionsComponent,
    TimeoutDialogComponent,
    DeleteAccountComponent,
    BookingDisplayCardComponent,
    BundleDisplayCardComponent,
    AddToCalendarComponent,
    CameraComponent,
    SignatureInputComponent,
    DrawableImageComponent,
    SwipeNotificationsComponent,
    PaymentDisplayCardComponent,
    AddressComponent,
    PaymentsComponent,
    ViewPaymentComponent,
    ViewPaymentOptionsComponent,
    PaymentDialogComponent,
    MedicalAidDetailsComponent,
    ContactDetailsComponent,
    MemberInformationComponent,
    PictureComponent,
    InputMaskDirective,
    LogButtonDirective,
    WhyFaceIdDialogComponent,
    ResetPasswordComponent,
    LoginComponent,
    RosterPatientDialogComponent,
    GoogleLocationComponent,
    BetaUserComponent,
    RetryDialogComponent,
    GeneralHealthComponent,
    OperationHistoryComponent,
    TreatmentHistoryComponent,
    OccupationalHistoryComponent,
    TravelHistoryComponent,
    FamilyHistoryComponent,
    VaccinationsComponent,
    AllergiesComponent,
    ChronicComponent,
    TranslateComponent,
    SameDayBookingDialogComponent,
    PeachpayEmbeddingComponent,
    MedicalHistoryComponent,
    NoteBannerComponent,
    DoctorReviewCardComponent,
  ],
  exports: [
    AngularSplitModule,
    ReactiveFormsModule,
    AddressFilterPipe,
    DistancePipe,
    TodayTomorrowPipe,
    ScrollContainerDirective,
    FormFieldDirective,
    InputFieldComponent,
    LoaderComponent,
    VerticalStepperComponent,
    DoctorCardComponent,
    PracticeCardComponent,
    CdkStepperModule,
    CustomCalendarComponent,
    CustomTabComponent,
    CustomTabContainerComponent,
    CustomTabBodyComponent,
    StepsHeaderComponent,
    StepsHeaderStepComponent,
    StepsHeaderCompleteDirective,
    StepsHeaderLabelDirective,
    SidebarNavComponent,
    SearchInputComponent,
    AutofocusDirective,
    SafePipe,
    CommunicationActionsComponent,
    GrowerComponent,
    ContactButtonComponent,
    TypeFilterComponent,
    NotificationListComponent,
    NotificationDetailComponent,
    NotificationFloaterComponent,
    TypeFilterDialogComponent,
    LoginSplashComponent,
    LoginFormComponent,
    LoginComponent,
    DoctorBookingCardComponent,
    DoctorListComponent,
    ProfilePictureComponent,
    LoggedOutHeaderComponent,
    BreadcrumbBarComponent,
    TermsAndConditionsDialogComponent,
    TermsAndConditionsComponent,
    BookingDisplayCardComponent,
    BundleDisplayCardComponent,
    AddToCalendarComponent,
    GoogleMapsModule,
    CameraComponent,
    SignatureInputComponent,
    DrawableImageComponent,
    SwipeNotificationsComponent,
    PaymentDisplayCardComponent,
    AddressComponent,
    PaymentsComponent,
    ViewPaymentComponent,
    ViewPaymentOptionsComponent,
    PaymentDialogComponent,
    MedicalAidDetailsComponent,
    ContactDetailsComponent,
    MemberInformationComponent,
    PictureComponent,
    InputMaskDirective,
    LogButtonDirective,
    ResetPasswordComponent,
    GoogleLocationComponent,
    BetaUserComponent,
    GeneralHealthComponent,
    OperationHistoryComponent,
    TreatmentHistoryComponent,
    OccupationalHistoryComponent,
    TravelHistoryComponent,
    FamilyHistoryComponent,
    VaccinationsComponent,
    AllergiesComponent,
    ChronicComponent,
    CustomDatePipe,
    GoogleSigninButtonModule,
    TranslateModule,
    TranslateComponent,
    PeachpayEmbeddingComponent,
    MedicalHistoryComponent,
    NoteBannerComponent,
  ],
  imports: [
    AngularSplitModule,
    CommonModule,
    TranslateModule,
    ReactiveFormsModule,
    MaterialModule,
    CdkStepperModule,
    A11yModule,
    RouterModule,
    GoogleMapsModule,
    WebcamModule,
    GoogleSigninButtonModule,
  ],
  providers: [
    DatePipe,
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useValue: {
        autoFocus: true,
        restoreFocus: true,
        closeOnNavigation: true,
      },
    },
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class SharedModule {}
